import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { useI18n } from '../contexts/I18nContext';
import { Modal } from '../components/Modal';
import { Mail, Lock, AlertCircle, AlertTriangle, Copy, CheckCircle } from 'lucide-react';

/**
 * Detects if the current browser is an in-app browser on iOS.
 * Android in-app browsers (Chrome Custom Tabs) usually support Google Sign-in,
 * but iOS WebViews (LinkedIn, Instagram) trigger Google's 403 disallowed_useragent error.
 */
const isIosInAppBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  
  // Check if it is a known in-app browser
  const rules = [
    'LinkedInApp',
    'Instagram',
    'FBAN',   // Facebook
    'FBAV',   // Facebook
    'Line',
    'Musical_ly', // TikTok
  ];
  const isInternal = new RegExp('(' + rules.join('|') + ')').test(ua);

  // Check if it is iOS (iPhone, iPad, iPod)
  const isIOS = /iPad|iPhone|iPod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Only return true if BOTH conditions are met
  return isInternal && isIOS;
};

/**
 * Parses OAuth/login errors and returns user-friendly messages.
 */
function getReadableErrorMessage(error: any, defaultMessage: string): string {
  const message = error?.message || error?.error_description || String(error);
  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes('user already registered') ||
    lowerMessage.includes('identity already exists') ||
    lowerMessage.includes('account already exists')
  ) {
    return 'An account with this email already exists with a different sign-in method. Please use your original sign-in method.';
  }

  if (lowerMessage.includes('invalid') && (lowerMessage.includes('credentials') || lowerMessage.includes('login'))) {
    return 'Invalid email or password. Please try again.';
  }

  if (lowerMessage.includes('email not confirmed') || lowerMessage.includes('confirm your email')) {
    return 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
  }

  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  if (lowerMessage.includes('oauth') && lowerMessage.includes('error')) {
    return 'Unable to connect to Google. Please try again or use email sign-in.';
  }

  if (lowerMessage.includes('user not found') || lowerMessage.includes('no user')) {
    return 'No account found with this email. Please sign up first.';
  }

  return defaultMessage || message;
}

// Google icon SVG component
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function LoginPage() {
  const { tt } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // State for in-app browser warning
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  // Set page title and noindex meta tag
  useEffect(() => {
    document.title = tt('Sign in') + ' - Llambada';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = 'Llambada';
      document.head.removeChild(meta);
    };
  }, [tt]);

  const clearError = () => setError('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setError(getReadableErrorMessage(error, 'Failed to sign in. Please check your credentials.'));
      setLoading(false);
    } else {
      // Navigate to returnTo URL if provided, otherwise go to dashboard
      navigate(returnTo || '/dashboard');
    }
  };

  const handleGoogleLogin = async () => {
    // Check if we are in an iOS environment that Google blocks
    if (isIosInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    setGoogleLoading(true);
    clearError();
    
    const { error } = await signInWithGoogle(returnTo || '/dashboard');
    
    if (error) {
      setError(getReadableErrorMessage(error, 'Failed to sign in with Google.'));
      setGoogleLoading(false);
    }
    // Note: If successful, the page will redirect, so we don't need to handle success
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLoading = loading || googleLoading;

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col items-center justify-center p-4">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <img 
            src="/logo.png" 
            alt="Llambada" 
            className="h-16 w-16 mx-auto rounded-2xl shadow-xl shadow-accent-500/25 mb-6"
          />
          <h1 className="font-display text-3xl font-bold text-theme-text mb-2">
            {tt('Welcome back')}
          </h1>
          <p className="text-theme-text-secondary">
            {tt('Sign in to continue to your projects')}
          </p>
        </div>

        <div className="card p-8 animate-slide-up">
          {error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error mb-5">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{tt(error)}</p>
            </div>
          )}

          {/* Google Sign-in Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="btn btn-secondary w-full py-3 mb-4 flex items-center justify-center gap-2"
          >
            {googleLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <GoogleIcon className="h-5 w-5" />
            )}
            {googleLoading ? tt('Connecting to Google...') : tt('Continue with Google')}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border-muted"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-theme-surface text-theme-text-muted">{tt('Or sign in with email')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Email address')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-11"
                  placeholder={tt('you@example.com')}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-theme-text-secondary">
                  {tt('Password')}
                </label>
                <Link to="/forgot-password" className="text-sm link">
                  {tt('Forgot password?')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-11"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full py-3"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                tt('Sign in with Email')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-theme-text-secondary">
              {tt("Don't have an account?")}{' '}
              <Link to={returnTo ? `/register?returnTo=${encodeURIComponent(returnTo)}` : '/register'} className="link">
                {tt('Create one')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* In-App Browser Warning Modal */}
      <Modal
        isOpen={showInAppWarning}
        onClose={() => setShowInAppWarning(false)}
        title={tt('Browser Not Supported')}
        size="sm"
      >
        <div className="flex flex-col gap-4 text-center">
          <div className="bg-amber-500/10 text-amber-600 dark:text-amber-400 p-4 rounded-lg flex flex-col items-center gap-2">
            <AlertTriangle className="h-8 w-8" />
            <p className="font-medium">{tt("Google Sign-in is blocked by this app's built-in browser.")}</p>
          </div>
          
          <div className="text-theme-text-secondary text-sm space-y-2">
            <p>{tt("To sign in with Google, you must open this page in your phone's main browser (Safari or Chrome).")}</p>
            <p className="font-semibold text-theme-text">{tt('Tap the menu icon (•••) in the corner and select "Open in Browser".')}</p>
          </div>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border-muted"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-theme-surface text-theme-text-muted">{tt('Or copy link manually')}</span>
            </div>
          </div>

          <button
            onClick={handleCopyLink}
            className="btn btn-secondary w-full flex items-center justify-center gap-2"
          >
            {copied ? (
              <>
                {tt('Copied!')} <CheckCircle className="h-4 w-4 text-theme-success" />
              </>
            ) : (
              <>
                {tt('Copy Page Link')} <Copy className="h-4 w-4" />
              </>
            )}
          </button>

          <button
            onClick={() => setShowInAppWarning(false)}
            className="btn btn-primary w-full"
          >
            {tt('Got it')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
